/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkAlt, faTimes } from "@fortawesome/free-solid-svg-icons";
import BoltIcon from "@mui/icons-material/Bolt";
import axios from "axios";

import { environment } from "../../environments/environment";
import { stripHtmlToText, sanitizeBody, formatLinkedInBody } from "../../utils/postBodyUtils";
import "./PostModal.css";

/**
 * Shared modal for viewing a lead/post. Used in Dashboard, Recent, JobDetail, and ExecutionDetail.
 * @param {Object} post - Lead/post object (may have body or bodySnippet, postLink/linkedInProfileURL, etc.)
 * @param {function} onClose - Called when modal is closed
 * @param {boolean} isLinkedIn - Whether the post is from LinkedIn
 * @param {boolean} showAiOutreach - Whether to show the "AI Suggest outreach" section (JobDetail/ExecutionDetail)
 * @param {string} jobObjective - Scout objective, for AI outreach (when showAiOutreach)
 * @param {string} accessToken - User auth token for API calls
 * @param {function} onMarkRead - Optional; called with postId when "Mark read" is clicked (Dashboard/Recent)
 */
function PostModal({ post, onClose, isLinkedIn, showAiOutreach = false, jobObjective = "", accessToken, onMarkRead }) {
  const [outreachSuggestion, setOutreachSuggestion] = useState("");
  const [outreachLoading, setOutreachLoading] = useState(false);

  useEffect(() => {
    setOutreachSuggestion("");
  }, [post?._id]);

  if (!post) return null;

  const openUrl = post.postLink || post.linkedInProfileURL || "#";
  const openLabel = isLinkedIn ? "Open in LinkedIn" : "Open in Reddit";
  const subreddit = post.subreddit_name
    ? post.subreddit_name.startsWith("r/")
      ? post.subreddit_name
      : `r/${post.subreddit_name}`
    : null;
  const title = isLinkedIn ? (post.postTitle || post.user || "Post") : (post.postTitle || post.user || "Post");
  const dateStr =
    post.post_created_at &&
    new Date(post.post_created_at).toLocaleString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

  // Prefer full post body; fall back to bodySnippet when API only sends a snippet
  const rawBody = typeof post.body === "string" && post.body.trim().length > 0 ? post.body : (post.bodySnippet || "");
  const hasContent = rawBody.trim().length > 0;
  const bodyContent = hasContent
    ? isLinkedIn
      ? formatLinkedInBody(rawBody)
      : sanitizeBody(rawBody)
    : null;

  const handleAiOutreach = async (e) => {
    e.stopPropagation();
    setOutreachLoading(true);
    try {
      const { data } = await axios.post(
        `${environment.BACKEND}/user/outreach-suggest`,
        {
          job_objective: jobObjective,
          lead_title: post.postTitle || "",
          lead_body_snippet: stripHtmlToText(post.body || "").slice(0, 1500),
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      setOutreachSuggestion(data.message || "No message generated.");
    } catch (err) {
      setOutreachSuggestion(err.response?.data?.hint || err.message || "Failed to generate. Try again.");
    } finally {
      setOutreachLoading(false);
    }
  };

  const modalContent = (
    <div
      className="post-modal-overlay"
      onClick={onClose}
      onKeyDown={(e) => e.key === "Escape" && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="post-modal-title"
    >
      <div className="post-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="shrink-0 flex items-start justify-between gap-4 p-5 sm:p-6 border-b border-slate-200">
          <div className="min-w-0 flex-1">
            <h2 id="post-modal-title" className="text-lg font-semibold text-slate-900 leading-snug">
              {title}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
              {!isLinkedIn && subreddit && (
                <a
                  href={`https://www.reddit.com/${subreddit}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-600 hover:text-emerald-700 font-medium no-underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {subreddit}
                </a>
              )}
              {post.user && <span>{isLinkedIn ? post.user : `u/${post.user}`}</span>}
              {dateStr && <span>{dateStr}</span>}
              {!isLinkedIn && post.upvotes != null && <span>{post.upvotes} upvotes</span>}
              {post.jobName && <span>{post.jobName}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a
              href={openUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 no-underline"
              onClick={(e) => e.stopPropagation()}
            >
              {openLabel}
              <FontAwesomeIcon icon={faExternalLinkAlt} className="h-3.5 w-3" />
            </a>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-slate-600 hover:bg-slate-100 transition-colors"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {(post.relevanceScore != null || post.relevanceReason) && (
          <div className="shrink-0 px-5 sm:px-6 py-3 bg-slate-50 border-b border-slate-100">
            <p className="text-sm font-medium text-slate-800">
              {post.relevanceScore != null && (
                <>Relevance: <span className="text-emerald-600">{post.relevanceScore}%</span></>
              )}
              {post.relevanceReason && (
                <span className="block mt-1 font-normal text-slate-600">
                  {post.relevanceScore != null ? "Why: " : "AI reason: "}{post.relevanceReason}
                </span>
              )}
            </p>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 modal-content-scrollbar post-modal-body min-h-[80px]">
          {bodyContent ? (
            <div className="post-modal-body-inner" dangerouslySetInnerHTML={{ __html: bodyContent }} />
          ) : (
            <p className="text-slate-500">No content.</p>
          )}
        </div>

        {showAiOutreach && (
          <div className="shrink-0 px-5 sm:px-6 py-4 border-t border-slate-200 bg-slate-50/80">
            {!outreachSuggestion ? (
              <button
                type="button"
                onClick={handleAiOutreach}
                disabled={outreachLoading}
                className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-white px-4 py-2.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <BoltIcon className="h-4 w-4" />
                {outreachLoading ? "Generating…" : "AI Suggest outreach"}
              </button>
            ) : (
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">Suggested message — copy and paste:</p>
                <textarea
                  readOnly
                  value={outreachSuggestion}
                  className="w-full min-h-[100px] rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 resize-y"
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(outreachSuggestion);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700"
                >
                  Copy
                </button>
              </div>
            )}
          </div>
        )}

        <div className="shrink-0 flex flex-wrap justify-end gap-3 p-4 sm:p-5 border-t border-slate-200 bg-slate-50/50">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Close
          </button>
          {onMarkRead && !post.is_read && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onMarkRead(post._id);
              }}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Mark read
            </button>
          )}
          <a
            href={openUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 no-underline"
            onClick={(e) => e.stopPropagation()}
          >
            {openLabel}
            <FontAwesomeIcon icon={faExternalLinkAlt} className="h-3.5 w-3" />
          </a>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default PostModal;
